import { useAppVersionStore } from '@/stores/appVersion';
import { useAuthStore } from '@/stores/auth';

export type SharePointConnectionHealth = {
    status:
        | 'healthy'
        | 'invalid_secret'
        | 'missing_permission'
        | 'unavailable'
        | 'not_configured';
    detail: string;
};

async function getSessionToken() {
    const auth = useAuthStore();
    const token = await auth.getSessionToken();
    return token;
}

async function getIssuer() {
    const auth = useAuthStore();
    return auth.issuer;
}

class ApiClient {
    host: string = import.meta.env.VITE_BACKEND_HOST;

    async fetchApi(
        url: string,
        options: Record<string, any>,
        allowedStatuses: number[] = []
    ) {
        if (url.startsWith('/')) {
            url = url.substring(1);
        }

        const baseUrl = this.host ? `https://${this.host}` : '';
        const response = await fetch(`${baseUrl}/api/${url}`, options);

        useAppVersionStore().recordServerBuild(
            response.headers.get('X-App-Build')
        );

        if (!response.ok && !allowedStatuses.includes(response.status)) {
            const error = await this.handleError(response);
            throw error;
        }
        return response;
    }

    async get(
        url: string,
        opts?: {
            extraHeaders?: Record<string, string>;
            allowNotModified?: boolean;
            signal?: AbortSignal;
        }
    ) {
        try {
            const sessionToken = await getSessionToken();
            const headers = {
                Authorization: `Bearer ${sessionToken}`,
                Issuer: await getIssuer(),
                ...opts?.extraHeaders
            };
            const options = {
                method: 'GET',
                headers,
                signal: opts?.signal
            };
            const allowedStatuses = opts?.allowNotModified ? [304] : [];
            return await this.fetchApi(url, options, allowedStatuses);
        } catch (error) {
            // Silently ignore AbortError - this is expected when requests are cancelled
            if (error instanceof Error && error.name === 'AbortError') {
                console.debug('Request aborted');
            } else {
                console.error('Error fetching data:', error);
            }
            throw error;
        }
    }

    async post(
        url: string,
        body: Record<string, any>,
        blob = false,
        signal?: AbortSignal
    ) {
        const sessionToken = await getSessionToken();
        const headers = {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${sessionToken}`,
            Issuer: await getIssuer()
        };
        const options: RequestInit = {
            method: 'POST',
            headers,
            body: JSON.stringify(body)
        };

        if (signal) {
            options.signal = signal;
        }

        try {
            const response = await this.fetchApi(url, options);

            if (blob) {
                return await response.blob();
            }
            return await response.json();
        } catch (error) {
            // Silently ignore AbortError - this is expected when requests are cancelled
            if (error instanceof Error && error.name === 'AbortError') {
                console.debug('Request aborted');
            } else if (error instanceof Error) {
                console.error('Error posting data:', error);
            }
            throw error;
        }
    }

    async postFormData(url: string, formData: FormData) {
        const sessionToken = await getSessionToken();
        const headers = {
            Authorization: `Bearer ${sessionToken}`,
            Issuer: await getIssuer()
        };
        const options = {
            method: 'POST',
            headers,
            body: formData
        };
        const response = await this.fetchApi(url, options);
        return await response.json();
    }

    async postSSERequest(
        url: string,
        body: Record<string, any>,
        signal: AbortSignal
    ) {
        const sessionToken = await getSessionToken();
        const headers = {
            Authorization: `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
            Accept: 'text/event-stream',
            Issuer: await getIssuer()
        };
        const options = {
            method: 'POST',
            headers,
            body: JSON.stringify(body),
            signal
        };
        try {
            const response = await this.fetchApi(url, options);
            if (!response.body) {
                throw new Error('Response body is null');
            }
            return response.body
                .pipeThrough(new TextDecoderStream())
                .getReader();
        } catch (error: any) {
            if (error.name === 'AbortError') {
                console.log('SSE connection aborted');
            } else {
                console.error('Failed to establish SSE connection:', error);
            }
        }
    }

    async postStream(
        url: string,
        body: Record<string, any>,
        signal?: AbortSignal
    ): Promise<ReadableStreamDefaultReader<Uint8Array> | undefined> {
        const sessionToken = await getSessionToken();
        const issuer = await getIssuer();
        const headers = {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${sessionToken}`,
            Issuer: issuer
        };
        const options: RequestInit = {
            method: 'POST',
            headers,
            body: JSON.stringify(body),
            signal
        };
        try {
            const response = await this.fetchApi(url, options);
            if (!response.body) {
                throw new Error('Response body is null');
            }
            return response.body.getReader();
        } catch (error) {
            if (error instanceof Error && error.name === 'AbortError') {
                console.debug('Stream request aborted');
                return undefined;
            }
            console.error('Streaming error :', error);
            throw error;
        }
    }

    async getSSE(url: string, signal?: AbortSignal) {
        const sessionToken = await getSessionToken();
        const headers = {
            Authorization: `Bearer ${sessionToken}`,
            Accept: 'text/event-stream',
            Issuer: await getIssuer()
        };
        const options: RequestInit = {
            method: 'GET',
            headers,
            signal
        };
        try {
            const response = await this.fetchApi(url, options);
            if (!response.body) {
                throw new Error('Response body is null');
            }
            return response.body
                .pipeThrough(new TextDecoderStream())
                .getReader();
        } catch (error: any) {
            if (error.name === 'AbortError') {
                console.debug('SSE connection aborted');
            } else {
                console.error('Failed to establish SSE connection:', error);
            }
            throw error;
        }
    }

    async delete(url: string, body: Record<string, any>) {
        const sessionToken = await getSessionToken();
        const headers = {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${sessionToken}`,
            Issuer: await getIssuer()
        };
        const options = {
            method: 'DELETE',
            headers,
            body: JSON.stringify(body)
        };

        try {
            const response = await this.fetchApi(url, options);
            return await this.parseJsonBody(response);
        } catch (error) {
            if (error instanceof Error) {
                console.error('Error in DELETE request:', error);
            }
            throw error;
        }
    }

    async patch(
        url: string,
        body: Record<string, any>,
        signal?: AbortSignal,
        allowedStatuses: number[] = []
    ) {
        const sessionToken = await getSessionToken();
        const headers = {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${sessionToken}`,
            Issuer: await getIssuer()
        };
        const options: RequestInit = {
            method: 'PATCH',
            headers,
            body: JSON.stringify(body)
        };

        if (signal) {
            options.signal = signal;
        }

        try {
            const response = await this.fetchApi(url, options, allowedStatuses);
            return await response.json();
        } catch (error) {
            // Silently ignore AbortError - this is expected when requests are cancelled
            if (error instanceof Error && error.name === 'AbortError') {
                console.debug('PATCH request aborted');
            } else if (error instanceof Error) {
                console.error('Error in PATCH request:', error);
            }
            throw error;
        }
    }

    /** Unlike `patch`, tolerates a 204: `parseJsonBody` returns undefined for it. */
    async put(
        url: string,
        body: Record<string, any>,
        allowedStatuses: number[] = []
    ) {
        const sessionToken = await getSessionToken();
        const response = await this.fetchApi(
            url,
            {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${sessionToken}`,
                    Issuer: await getIssuer()
                },
                body: JSON.stringify(body)
            },
            allowedStatuses
        );
        return await this.parseJsonBody(response);
    }

    async parseJsonBody(response: Response) {
        if (response.status === 204) {
            return undefined;
        }
        const text = await response.text();
        return text ? JSON.parse(text) : undefined;
    }

    async handleError(response: Response) {
        let errorMessage = `HTTP error ${response.status}`;
        try {
            const errorData = await response.json();
            errorMessage = errorData.detail || errorMessage;
        } catch {
            // Response wasn't JSON - In that case, we return the generic error message
        }
        const error: any = new Error(errorMessage);
        error.status = response.status;
        return error;
    }

    async getSharePointCredentials(tenantId: string) {
        const url = `/sharepoint/credentials?${'tenant_id'}=${encodeURIComponent(
            tenantId
        )}`;
        const response = await this.get(url);
        return await response.json();
    }

    async getSharePointConnectionHealth(
        tenantId: string
    ): Promise<SharePointConnectionHealth> {
        const response = await this.get(
            `/sharepoint/connection_health?tenant_id=${encodeURIComponent(tenantId)}`
        );
        return await response.json();
    }

    async getMsalCredentials() {
        const response = await this.get('/msal_credentials');
        return await response.json();
    }
}

const apiClient = new ApiClient();
export default apiClient;
